import { ReactNode } from 'react';
import styled from 'styled-components';

import { type ScrollConfig } from 'components/insights/careTransitions/networkPerformance/slideout/headers/chartHelpers';
import CircularProgress from 'components/shared/CircularProgress';
import { colors } from 'styles/theme/colors';

import { TilesGrid } from './statTiles';

type Props = {
  tiles: ReactNode;
  // Number of tile columns — set to the tile count so a 2-tile header stretches across the row
  // instead of leaving an empty third column (matches the biz dev slideout).
  columns?: number;
  chartTitle: string;
  chartLoading: boolean;
  showChart?: boolean;
  isEmpty?: boolean;
  emptyMessage?: string;
  scrollConfig?: ScrollConfig;
  // Height (px) the populated chart occupies. The loading/empty placeholder reserves it so the card
  // doesn't collapse to a short spinner and jump when data lands (or on a View By switch). When
  // omitted, the legacy 120px placeholder is used and the scroll container is left to size itself.
  chartHeight?: number;
  children: ReactNode;
};

// A horizontally-scrolling chart shows a bottom scrollbar only once its bars overflow, which would
// otherwise add ~8px and jump the card. When a caller declares its chart height we reserve that band
// so the height is constant whether or not the scrollbar is present.
const SCROLLBAR_ALLOWANCE = 8;

export default function MetricDetailLayout({
  tiles,
  columns = 3,
  chartTitle,
  chartLoading,
  showChart = true,
  isEmpty = false,
  emptyMessage = '\u2014',
  scrollConfig,
  chartHeight,
  children,
}: Props) {
  // Only reserve the scrollbar band for a caller-sized, scrolling chart (inbound). Charts without a
  // declared height (e.g. network performance) keep the legacy auto-sizing.
  const scrollHeight = scrollConfig && chartHeight != null ? chartHeight + SCROLLBAR_ALLOWANCE : undefined;
  const placeholderHeight = (chartHeight ?? 120) + (scrollHeight != null ? SCROLLBAR_ALLOWANCE : 0);

  return (
    <Wrapper>
      <TilesGrid $columns={columns}>{tiles}</TilesGrid>
      {showChart && (
        <ChartCard>
          <ChartTitle>{chartTitle}</ChartTitle>
          {chartLoading ? (
            <ChartPlaceholder $height={placeholderHeight}>
              <CircularProgress color={colors.blue100} size={28} thickness={3} />
            </ChartPlaceholder>
          ) : isEmpty ? (
            <ChartPlaceholder $height={placeholderHeight}>{emptyMessage}</ChartPlaceholder>
          ) : scrollConfig ? (
            <>
              <ScrollContainer $height={scrollHeight}>
                <ChartContent $minWidth={scrollConfig.minWidth}>{children}</ChartContent>
              </ScrollContainer>
              {scrollConfig.legend && (
                <ChartLegend>
                  <LegendDot $color={scrollConfig.legend.color} />
                  {scrollConfig.legend.label}
                </ChartLegend>
              )}
            </>
          ) : (
            children
          )}
        </ChartCard>
      )}
    </Wrapper>
  );
}

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const ChartCard = styled.div`
  border: 1px solid ${({ theme }) => theme.colors.black10};
  border-radius: 4px;
  padding: 20px 22px;
`;

const ScrollContainer = styled.div<{ $height?: number }>`
  overflow-x: auto;
  overflow-y: hidden;
  ${({ $height }) => $height != null && `height: ${$height}px;`}

  &::-webkit-scrollbar {
    height: 0.5em;
  }
  &::-webkit-scrollbar-thumb {
    background-color: ${({ theme }) => theme.colors.black50};
    border-radius: 5pt;
  }
`;

const ChartContent = styled.div<{ $minWidth: number }>`
  min-width: ${({ $minWidth }) => $minWidth}px;
`;

const ChartTitle = styled.div`
  font-size: 14px;
  font-weight: 500;
  color: ${({ theme }) => theme.colors.black100};
  margin-bottom: 16px;
`;

const ChartLegend = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  font-size: 12px;
  color: ${({ theme }) => theme.colors.black75};
  padding-top: 8px;
`;

const LegendDot = styled.span<{ $color: string }>`
  width: 10px;
  height: 10px;
  border-radius: 50%;
  background-color: ${({ $color }) => $color};
  flex-shrink: 0;
`;

const ChartPlaceholder = styled.div<{ $height: number }>`
  height: ${({ $height }) => $height}px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 12px;
  color: ${({ theme }) => theme.colors.black25};
`;
